import {
  AnalyticsPendingRegistrationRecord,
  AnalyticsInvoicingRecord,
  AnalyticsTimesheetRecord,
  AnalyticsPayrollRecord,
  AnalyticsAgencyRecord,
} from '@nxsys/types/models/analytics';
import type { ICompany } from '@nxsys/types/models/company';
import type { PaginationType } from '@nxsys/types/models/Common';
import type {
  GetAnalyticsPayrollCostResponse,
  GetAnalyticsPaymentsResponse,
  GetAnalyticsPayslipsProcessedResponse,
  GetAnalyticsRegistrationResponse,
  RequiredAlertsCounts,
} from '../types';
import { createSlice } from '@reduxjs/toolkit';
import { analyticsActs } from '../actions';
import { showToast } from '@nxsys/utils/toast';
import { appIntl } from '@nxsys/helpers/Common';

export type AnalyticsState = {
  payrollData: {
    data: AnalyticsPayrollRecord[];
    loading: boolean;
  };
  registrationsData: {
    pending: {
      data: AnalyticsPendingRegistrationRecord[];
      totalDocs: number;
      size: number;
      current: number;
      loading: boolean;
    };
    new: {
      data: AnalyticsPendingRegistrationRecord[];
      totals: number;
      loading: boolean;
      compare: number;
    };
  };
  agenciesData: {
    data: AnalyticsAgencyRecord[];
    loading: boolean;
  };
  invoicing: {
    data: AnalyticsInvoicingRecord;
    loading: boolean;
  };
  timesheetData: {
    data: AnalyticsTimesheetRecord;
    loading: boolean;
  };
  pensionData: {
    pensionOptOut: number;
    documentsNearingExpiry: number;
    myFutureFundOptOut: number;
    privatePensionInactive: number;
    loading: boolean;
  };
  payrollCostData: {
    data: GetAnalyticsPayrollCostResponse;
    loading: boolean;
  };
  paymentsData: {
    data: GetAnalyticsPaymentsResponse | null;
    loading: boolean;
  };
  requiredAlertsData: {
    data: RequiredAlertsCounts | null;
    loading: boolean;
  };
  registrationSummaryData: {
    data: Pick<
      GetAnalyticsRegistrationResponse,
      'totalsNew' | 'totalsPending'
    > | null;
    loading: boolean;
  };
  payslipsProcessedData: {
    data: GetAnalyticsPayslipsProcessedResponse | null;
    loading: boolean;
  };
  companiesNoPayrollData: {
    data: ICompany[];
    total: number;
    loading: boolean;
  };
  companiesNoPayrollListData: {
    data: ICompany[];
    pagination: PaginationType;
    loading: boolean;
  };
};

const initialState: AnalyticsState = {
  payrollData: {
    data: [],
    loading: false,
  },
  registrationsData: {
    pending: {
      data: [],
      totalDocs: 0,
      size: 20,
      current: 0,
      loading: false,
    },
    new: {
      data: [],
      totals: 0,
      loading: false,
      compare: 0,
    },
  },
  agenciesData: {
    data: [],
    loading: false,
  },
  invoicing: {
    data: {
      totalInvoices: 0,
      totalPaid: 0,
      totalUnpaid: 0,
    },
    loading: false,
  },
  timesheetData: {
    data: {
      dataMargin: {
        data: [],
        totals: 0,
      },
      dataTimesheet: {
        data: [],
        totals: 0,
      },
      margin: {
        totals: 0,
        compare: 0,
      },
      netPay: {
        totals: 0,
        compare: 0,
      },
      timesheet: {
        totalTimesheetsReceived: 0,
      },
      timesheetValue: {
        totals: 0,
        compare: 0,
      },
    },
    loading: false,
  },
  pensionData: {
    pensionOptOut: 0,
    documentsNearingExpiry: 0,
    myFutureFundOptOut: 0,
    privatePensionInactive: 0,
    loading: false,
  },
  payrollCostData: {
    data: {},
    loading: false,
  },
  paymentsData: {
    data: null,
    loading: false,
  },
  requiredAlertsData: {
    data: null,
    loading: false,
  },
  registrationSummaryData: {
    data: null,
    loading: false,
  },
  payslipsProcessedData: {
    data: null,
    loading: false,
  },
  companiesNoPayrollData: {
    data: [],
    total: 0,
    loading: false,
  },
  companiesNoPayrollListData: {
    data: [],
    pagination: {
      current: 0,
      pageSize: 20,
      totalDocs: 0,
    },
    loading: false,
  },
};

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Get Payroll Data
    builder.addCase(analyticsActs.getAnalyticsPayrollData.pending, (state) => {
      state.payrollData.loading = true;
    });
    builder.addCase(
      analyticsActs.getAnalyticsPayrollData.fulfilled,
      (state, action) => {
        state.payrollData.loading = false;
        state.payrollData.data = action.payload.payroll;
      },
    );
    builder.addCase(analyticsActs.getAnalyticsPayrollData.rejected, (state) => {
      state.payrollData.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'analytics.getPayrollDataError',
        }),
      });
    });

    // Get Payroll Values (filtered)
    builder.addCase(analyticsActs.getAnalyticsPayrollValues.pending, (state) => {
      state.payrollData.loading = true;
    });
    builder.addCase(
      analyticsActs.getAnalyticsPayrollValues.fulfilled,
      (state, action) => {
        state.payrollData.loading = false;
        // API is expected to return:
        // { chart: AnalyticsPayrollRecord[]; dateFrom; dateTo; success }
        const payload: any = action.payload;
        state.payrollData.data = payload?.chart ?? payload?.payroll ?? [];
      },
    );
    builder.addCase(
      analyticsActs.getAnalyticsPayrollValues.rejected,
      (state) => {
        state.payrollData.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'analytics.getPayrollDataError',
          }),
        });
      },
    );

    // Get Registration New
    builder.addCase(
      analyticsActs.getAnalyticsRegistrationNew.pending,
      (state) => {
        state.registrationsData.new.loading = true;
      },
    );

    builder.addCase(
      analyticsActs.getAnalyticsRegistrationNew.fulfilled,
      (state, action) => {
        state.registrationsData.new.loading = false;
        state.registrationsData.new.data = action.payload.newRegistration.data;
        state.registrationsData.new.totals =
          action.payload.newRegistration.totals;
        state.registrationsData.new.compare =
          action.payload.newRegistration.compare;
      },
    );

    builder.addCase(
      analyticsActs.getAnalyticsRegistrationNew.rejected,
      (state) => {
        state.registrationsData.new.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'analytics.getRegistrationNewError',
          }),
        });
      },
    );

    // Get Registration Pending
    builder.addCase(
      analyticsActs.getAnalyticsRegistrationPending.pending,
      (state) => {
        state.registrationsData.pending.loading = true;
      },
    );

    builder.addCase(
      analyticsActs.getAnalyticsRegistrationPending.fulfilled,
      (state, action) => {
        state.registrationsData.pending.loading = false;
        state.registrationsData.pending.data = action.payload.data;
        state.registrationsData.pending.totalDocs = action.payload.totalDocs;
        // Update pagination info from the request meta
        if (action.meta.arg) {
          state.registrationsData.pending.current = action.meta.arg.page || 0;
          state.registrationsData.pending.size = action.meta.arg.size || 20;
        }
      },
    );

    builder.addCase(
      analyticsActs.getAnalyticsRegistrationPending.rejected,
      (state) => {
        state.registrationsData.pending.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'analytics.getRegistrationPendingError',
          }),
        });
      },
    );

    // Get Registration (summary)
    builder.addCase(analyticsActs.getAnalyticsRegistration.pending, (state) => {
      state.registrationSummaryData.loading = true;
    });

    builder.addCase(
      analyticsActs.getAnalyticsRegistration.fulfilled,
      (state, action) => {
        state.registrationSummaryData.loading = false;
        state.registrationSummaryData.data = action.payload
          ? {
              totalsNew: action.payload.totalsNew,
              totalsPending: action.payload.totalsPending,
            }
          : null;
      },
    );

    builder.addCase(
      analyticsActs.getAnalyticsRegistration.rejected,
      (state) => {
        state.registrationSummaryData.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'analytics.getRegistrationError',
          }),
        });
      },
    );

    // Get Invoicing
    builder.addCase(analyticsActs.getAnalyticsInvoicing.pending, (state) => {
      state.invoicing.loading = true;
    });

    builder.addCase(
      analyticsActs.getAnalyticsInvoicing.fulfilled,
      (state, action) => {
        state.invoicing.loading = false;
        state.invoicing.data.totalInvoices = action.payload.totalInvoices;
        state.invoicing.data.totalPaid = action.payload.totalPaid;
        state.invoicing.data.totalUnpaid = action.payload.totalUnpaid;
      },
    );

    builder.addCase(analyticsActs.getAnalyticsInvoicing.rejected, (state) => {
      state.invoicing.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'analytics.getInvoicingError',
        }),
      });
    });

    // Get Invoicing Values
    builder.addCase(
      analyticsActs.getAnalyticsInvoicingValues.pending,
      (state) => {
        state.invoicing.loading = true;
      },
    );

    builder.addCase(
      analyticsActs.getAnalyticsInvoicingValues.fulfilled,
      (state, action) => {
        state.invoicing.loading = false;
        state.invoicing.data.totalInvoices = action.payload.totalInvoices;
        state.invoicing.data.totalPaid = action.payload.totalPaid;
        state.invoicing.data.totalUnpaid = action.payload.totalUnpaid;
      },
    );

    builder.addCase(
      analyticsActs.getAnalyticsInvoicingValues.rejected,
      (state) => {
        state.invoicing.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'analytics.getInvoicingError',
          }),
        });
      },
    );

    // Get Payments
    builder.addCase(analyticsActs.getAnalyticsPayments.pending, (state) => {
      state.paymentsData.loading = true;
    });

    builder.addCase(
      analyticsActs.getAnalyticsPayments.fulfilled,
      (state, action) => {
        state.paymentsData.loading = false;
        state.paymentsData.data = action.payload;
      },
    );

    builder.addCase(analyticsActs.getAnalyticsPayments.rejected, (state) => {
      state.paymentsData.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'analytics.getPaymentsError',
        }),
      });
    });

    // Get Timesheet
    builder.addCase(analyticsActs.getAnalyticsTimesheet.pending, (state) => {
      state.timesheetData.loading = true;
    });

    builder.addCase(
      analyticsActs.getAnalyticsTimesheet.fulfilled,
      (state, action) => {
        state.timesheetData.loading = false;
        state.timesheetData.data = action.payload;
      },
    );

    builder.addCase(analyticsActs.getAnalyticsTimesheet.rejected, (state) => {
      state.timesheetData.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'analytics.getTimesheetError',
        }),
      });
    });

    // Get Agency
    builder.addCase(analyticsActs.getAnalyticsAgency.pending, (state) => {
      state.agenciesData.loading = true;
    });

    builder.addCase(
      analyticsActs.getAnalyticsAgency.fulfilled,
      (state, action) => {
        state.agenciesData.loading = false;
        state.agenciesData.data = action.payload.data;
      },
    );

    builder.addCase(analyticsActs.getAnalyticsAgency.rejected, (state) => {
      state.agenciesData.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'analytics.getAgencyError',
        }),
      });
    });

    // Get Agency Invoice (filtered)
    builder.addCase(
      analyticsActs.getAnalyticsAgencyInvoice.pending,
      (state) => {
        state.agenciesData.loading = true;
      },
    );

    builder.addCase(
      analyticsActs.getAnalyticsAgencyInvoice.fulfilled,
      (state, action) => {
        state.agenciesData.loading = false;
        state.agenciesData.data = action.payload.data;
      },
    );

    builder.addCase(
      analyticsActs.getAnalyticsAgencyInvoice.rejected,
      (state) => {
        state.agenciesData.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'analytics.getAgencyError',
          }),
        });
      },
    );

    // Get Payroll Cost
    builder.addCase(analyticsActs.getAnalyticsPayrollCost.pending, (state) => {
      state.payrollCostData.loading = true;
    });

    builder.addCase(
      analyticsActs.getAnalyticsPayrollCost.fulfilled,
      (state, action) => {
        state.payrollCostData.loading = false;
        state.payrollCostData.data = action.payload;
      },
    );

    builder.addCase(analyticsActs.getAnalyticsPayrollCost.rejected, (state) => {
      state.payrollCostData.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'analytics.getPayrollCostError',
        }),
      });
    });

    // Get Pension Document
    builder.addCase(
      analyticsActs.getAnalyticsPensionDocument.pending,
      (state) => {
        state.pensionData.loading = true;
      },
    );

    builder.addCase(
      analyticsActs.getAnalyticsPensionDocument.fulfilled,
      (state, action) => {
        state.pensionData.loading = false;
        state.pensionData.pensionOptOut =
          action.payload.data.pensionOptOut ?? 0;
        state.pensionData.documentsNearingExpiry =
          action.payload.data.documentsNearingExpiry ?? 0;
        state.pensionData.myFutureFundOptOut =
          action.payload.data.myFutureFundOptOut ?? 0;
        state.pensionData.privatePensionInactive =
          action.payload.data.privatePensionInactive ?? 0;
      },
    );

    builder.addCase(
      analyticsActs.getAnalyticsPensionDocument.rejected,
      (state) => {
        state.pensionData.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'analytics.getPensionDocumentError',
          }),
        });
      },
    );

    // Get Required Alerts
    builder.addCase(
      analyticsActs.getAnalyticsRequiredAlerts.pending,
      (state) => {
        state.requiredAlertsData.loading = true;
      },
    );

    builder.addCase(
      analyticsActs.getAnalyticsRequiredAlerts.fulfilled,
      (state, action) => {
        state.requiredAlertsData.loading = false;
        state.requiredAlertsData.data = action.payload?.data ?? null;
      },
    );

    builder.addCase(
      analyticsActs.getAnalyticsRequiredAlerts.rejected,
      (state) => {
        state.requiredAlertsData.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'analytics.getRequiredAlertsError',
          }),
        });
      },
    );

    // Get Payslips Processed
    builder.addCase(
      analyticsActs.getAnalyticsPayslipsProcessed.pending,
      (state) => {
        state.payslipsProcessedData.loading = true;
      },
    );

    builder.addCase(
      analyticsActs.getAnalyticsPayslipsProcessed.fulfilled,
      (state, action) => {
        state.payslipsProcessedData.loading = false;
        state.payslipsProcessedData.data = action.payload ?? null;
      },
    );

    builder.addCase(
      analyticsActs.getAnalyticsPayslipsProcessed.rejected,
      (state) => {
        state.payslipsProcessedData.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'analytics.getPayslipsProcessedError',
          }),
        });
      },
    );

    // Get Companies No Payroll
    builder.addCase(
      analyticsActs.getAnalyticsCompaniesNoPayroll.pending,
      (state) => {
        state.companiesNoPayrollData.loading = true;
      },
    );

    builder.addCase(
      analyticsActs.getAnalyticsCompaniesNoPayroll.fulfilled,
      (state, action) => {
        state.companiesNoPayrollData.loading = false;
        state.companiesNoPayrollData.data = action.payload?.data ?? [];
        state.companiesNoPayrollData.total =
          action.payload?.pagination?.totalDocs ??
          (action.payload?.data?.length ?? 0);
      },
    );

    builder.addCase(
      analyticsActs.getAnalyticsCompaniesNoPayroll.rejected,
      (state) => {
        state.companiesNoPayrollData.loading = false;
        state.companiesNoPayrollData.data = [];
        state.companiesNoPayrollData.total = 0;
      },
    );

    // Get Companies No Payroll List (modal with pagination)
    builder.addCase(
      analyticsActs.getAnalyticsCompaniesNoPayrollList.pending,
      (state) => {
        state.companiesNoPayrollListData.loading = true;
      },
    );

    builder.addCase(
      analyticsActs.getAnalyticsCompaniesNoPayrollList.fulfilled,
      (state, action) => {
        state.companiesNoPayrollListData.loading = false;
        state.companiesNoPayrollListData.data = action.payload?.data ?? [];
        state.companiesNoPayrollListData.pagination = {
          current:
            action.payload?.pagination?.current ?? action.meta.arg?.page ?? 0,
          pageSize:
            action.payload?.pagination?.pageSize ?? action.meta.arg?.size ?? 20,
          totalDocs:
            action.payload?.pagination?.totalDocs ??
            (action.payload?.data?.length ?? 0),
        };
      },
    );

    builder.addCase(
      analyticsActs.getAnalyticsCompaniesNoPayrollList.rejected,
      (state) => {
        state.companiesNoPayrollListData.loading = false;
      },
    );
  },
});

export const analyticsReducer = analyticsSlice.reducer;
export const analyticsAction = {
  ...analyticsSlice.actions,
  ...analyticsActs,
};
